import {
  DocBody,
  DocHeader,
  LogoBox,
  DocTitle,
  SectionHeader,
  SectionTitle,
  AgreeGroup,
  AgreeText,
  AgreeCheck,
  TableWrap,
  TableCol,
  FullCol,
  LabelCell,
  ValueCell,
  TermBody,
  SignDateRow,
  SignDateBlank,
  SignRow,
  SignItem,
  SignLabel,
  SignNameBlank,
  SignStampBox,
  ThanksText,
  ThanksStrong,
  ThanksNormal,
} from './ElectronicContractPreviewStyled';

// 개인정보 표 행 — 4컬럼 (라벨/값/라벨/값)
const PERSONAL_ROWS = [
  { leftLabel: '성명', rightLabel: '성별' },
  { leftLabel: '연락처', rightLabel: '생년월일' },
  { leftLabel: '주소', rightLabel: '담당자' },
];

// 상품정보 표 행
const PRODUCT_ROWS = [
  { leftLabel: '상품명', rightLabel: '상품분류' },
  { leftLabel: '잔여횟수', rightLabel: '잔여기간' },
];

// 환불 금액 표 행 (좌: 라벨/값, 우: 라벨/값(원 단위)) — 환불금액만 강조(danger) 표시
const REFUND_AMOUNT_ROWS = [
  { leftLabel: '판매금액', rightLabel: '이용금액' },
  { leftLabel: '위약금', rightLabel: '환불금액', dangerRight: true },
];

// 환불 계약서 미리보기 (Figma node 7606:8790)
// 환불수단/영수증 섹션은 실제 계약 시점(ContractPdfService.generateAndUploadPdf)에만 결정되는
// 동적 데이터라 템플릿 단계에선 알 수 없다. 그 생성 로직과 모양을 맞춘다 — 환불수단은 항상
// "환불수단 | 0원" 한 줄(상세 칼럼 없음), 영수증 섹션은 조건 불충족(계약 전이라 당연히 그렇다)
// 시 통째로 생략되므로 이 미리보기에도 그리지 않는다.
const ElectronicContractPreviewPageRefund = ({ name, branchName, logoUrl, sealUrl, termSections = [] }) => (
  <DocBody>
    {/* 로고 + 제목 */}
    <DocHeader>
      <LogoBox>{logoUrl && <img src={logoUrl} alt="로고" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}</LogoBox>
      <DocTitle>{name || '바디스위치 피트니스 환불 계약서'}</DocTitle>
      <div style={{ width: 120, flexShrink: 0 }} />
    </DocHeader>

    {/* 개인정보 */}
    <FullCol>
      <SectionHeader rounded="top">
        <SectionTitle>개인정보</SectionTitle>
      </SectionHeader>
      {PERSONAL_ROWS.map(row => (
        <TableWrap key={row.leftLabel}>
          <TableCol fixed={100}>
            <LabelCell>{row.leftLabel}</LabelCell>
          </TableCol>
          <TableCol>
            <ValueCell />
          </TableCol>
          <TableCol fixed={100}>
            <LabelCell>{row.rightLabel}</LabelCell>
          </TableCol>
          <TableCol>
            <ValueCell />
          </TableCol>
        </TableWrap>
      ))}
    </FullCol>

    {/* 상품정보 */}
    <FullCol>
      <SectionHeader rounded="top">
        <SectionTitle>상품정보</SectionTitle>
      </SectionHeader>
      {PRODUCT_ROWS.map(row => (
        <TableWrap key={row.leftLabel}>
          <TableCol fixed={100}>
            <LabelCell>{row.leftLabel}</LabelCell>
          </TableCol>
          <TableCol>
            <ValueCell />
          </TableCol>
          <TableCol fixed={100}>
            <LabelCell>{row.rightLabel}</LabelCell>
          </TableCol>
          <TableCol>
            <ValueCell />
          </TableCol>
        </TableWrap>
      ))}
    </FullCol>

    {/* 환불 금액 */}
    <FullCol>
      <SectionHeader rounded="top">
        <SectionTitle>환불 금액</SectionTitle>
      </SectionHeader>
      {REFUND_AMOUNT_ROWS.map(row => (
        <TableWrap key={row.leftLabel}>
          <TableCol fixed={100}>
            <LabelCell>{row.leftLabel}</LabelCell>
          </TableCol>
          <TableCol>
            <ValueCell />
          </TableCol>
          <TableCol fixed={100}>
            <LabelCell danger={row.dangerRight}>{row.rightLabel}</LabelCell>
          </TableCol>
          <TableCol>
            <ValueCell align="flex-end" danger={row.dangerRight} />
          </TableCol>
        </TableWrap>
      ))}
      <TableWrap>
        <TableCol fixed={100}>
          <LabelCell>환불사유</LabelCell>
        </TableCol>
        <TableCol>
          <ValueCell />
        </TableCol>
      </TableWrap>
    </FullCol>

    {/* 환불 수단 — 실제 계약서도 라인이 없으면 "환불수단 | 0원" 한 줄(상세 칼럼 없음)로만 나온다 */}
    <FullCol>
      <SectionHeader rounded="top">
        <SectionTitle>환불 수단</SectionTitle>
      </SectionHeader>
      <TableWrap>
        <TableCol fixed={100}>
          <LabelCell>환불수단</LabelCell>
        </TableCol>
        <TableCol>
          <ValueCell align="flex-end" />
        </TableCol>
      </TableWrap>
    </FullCol>

    {/* 영수증 정보 — 템플릿 단계는 현금/계좌이체 라인도 영수증 발행 선택도 없어 조건을 항상
        충족하지 못하므로, 실제 계약서와 마찬가지로 섹션 자체를 그리지 않는다. */}

    {/* 약관 섹션 — 선택된 조항만 표시 */}
    {termSections.map(term => (
      <FullCol key={term.title}>
        <SectionHeader rounded="top">
          <SectionTitle>{term.title}</SectionTitle>
          <AgreeGroup>
            <AgreeText>아래 약관을 확인하였으며 동의합니다.</AgreeText>
            <AgreeCheck />
          </AgreeGroup>
        </SectionHeader>
        <TermBody>{term.body}</TermBody>
      </FullCol>
    ))}

    {/* 서명 */}
    <SignDateRow>
      <SignDateBlank />
      <span>년</span>
      <SignDateBlank />
      <span>월</span>
      <SignDateBlank />
      <span>일</span>
    </SignDateRow>
    <SignRow>
      <SignItem>
        <SignLabel>담당자</SignLabel>
        <SignNameBlank />
        <SignStampBox filled>
          {sealUrl && <img src={sealUrl} alt="직인" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
        </SignStampBox>
      </SignItem>
      <SignItem>
        <SignLabel>계약자</SignLabel>
        <SignNameBlank />
        <SignStampBox />
      </SignItem>
    </SignRow>

    {/* 감사 문구 */}
    <ThanksText>
      <ThanksStrong>{branchName || '지점'}</ThanksStrong>
      <ThanksNormal> 환불 신청이 정상적으로 접수되었습니다.</ThanksNormal>
    </ThanksText>
  </DocBody>
);

export default ElectronicContractPreviewPageRefund;
